// src/components/traffic/DailyTraffic.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Grid, Card, CardContent, CardHeader, Typography, Button,
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
    Paper, Chip, LinearProgress
} from '@mui/material';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    Legend, ResponsiveContainer
} from 'recharts';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { get } from '../../services/apiService';

function DailyTraffic({ clientId, days = 7, setError }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchDailyData = useCallback(async () => {
        try {
            setLoading(true);

            // Si no hay cliente seleccionado, no hacemos la petición
            if (!clientId) {
                setData([]);
                setLastUpdated(new Date());
                setError('Seleccione un cliente para ver su tráfico diario.');
                return;
            }

            const url = `/api/traffic/daily/${clientId}?days=${days}`;

            const result = await get(url);
            setData(result);
            setLastUpdated(new Date());
        } catch (error) {
            console.error('Error al obtener datos diarios:', error);
            setError('No se pudieron cargar los datos diarios. Por favor, inténtelo de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [clientId, days, setError]);

    // Load data when component mounts or dependencies change
    useEffect(() => {
        fetchDailyData();
    }, [fetchDailyData]);

    // Format data for chart
    const prepareChartData = () => {
        // If no specific client is selected, group by day
        if (!clientId) {
            // Group by day and sum values
            const groupedByDay = data.reduce((acc, item) => {
                const day = item.date;
                if (!acc[day]) {
                    acc[day] = {
                        day,
                        downloadGB: 0,
                        uploadGB: 0,
                        totalGB: 0
                    };
                }

                // Sum values in GB (assuming they come in that unit)
                const downloadGB = parseFloat(item.download || 0);
                const uploadGB = parseFloat(item.upload || 0);

                acc[day].downloadGB += downloadGB;
                acc[day].uploadGB += uploadGB;
                acc[day].totalGB += downloadGB + uploadGB;

                return acc;
            }, {});

            return Object.values(groupedByDay)
                .sort((a, b) => new Date(a.day) - new Date(b.day))
                .map(item => ({
                    ...item,
                    // Format to exactly 2 decimals to avoid long floating point numbers
                    downloadGB: Math.round(item.downloadGB * 100) / 100,
                    uploadGB: Math.round(item.uploadGB * 100) / 100,
                    totalGB: Math.round(item.totalGB * 100) / 100
                }));
        }

        // If there's a specific client, just format the dates - with fixed 2 decimals
        return data
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map(item => {
                const download = parseFloat(item.download || 0);
                const upload = parseFloat(item.upload || 0);
                return {
                    day: item.date,
                    downloadGB: Math.round(download * 100) / 100,
                    uploadGB: Math.round(upload * 100) / 100,
                    totalGB: Math.round((download + upload) * 100) / 100
                };
            });
    };

    const chartData = prepareChartData();

    // Function to update data
    const handleRefresh = async () => {
        await fetchDailyData();
    };

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 2
            }}>
                <Typography variant="h6" component="h2" fontWeight="500">
                    Tráfico Diario (Últimos {days} días)
                    {lastUpdated && (
                        <Typography variant="body2" component="span" sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 }, color: 'text.secondary', display: { xs: 'block', sm: 'inline' } }}>
                            Datos cargados: {lastUpdated.toLocaleTimeString()}
                        </Typography>
                    )}
                </Typography>
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading || !clientId}
                    variant="outlined"
                    sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                >
                    Actualizar
                </Button>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Mensaje cuando no hay cliente seleccionado */}
            {!clientId && (
                <Paper
                    sx={{
                        p: 3,
                        mb: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '200px',
                        backgroundColor: 'rgb(246, 248, 250)'
                    }}
                >
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No hay cliente seleccionado
                    </Typography>
                    <Typography variant="body1" color="text.secondary" align="center">
                        Por favor, seleccione un cliente en el campo superior para ver su tráfico diario.
                    </Typography>
                </Paper>
            )}

            <Grid container spacing={3}>
                {/* Daily consumption chart */}
                <Grid item xs={12}>
                    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)', minWidth: 0 }}>
                        <CardHeader
                            title="Consumo Diario (GB)"
                            titleTypographyProps={{ fontWeight: '500' }}
                        />
                        <CardContent>
                            <ResponsiveContainer width="100%" height={400}>
                                <AreaChart
                                    data={chartData}
                                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ fontSize: 12, fill: '#666' }}
                                        tickMargin={8}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12, fill: '#666' }}
                                        tickMargin={8}
                                    />
                                    <RechartsTooltip
                                        formatter={(value) => [`${value} GB`, '']}
                                        contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: 'none',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}
                                        labelStyle={{ color: '#333', fontWeight: 'bold' }}
                                    />
                                    <Legend />
                                    <Area
                                        type="monotone"
                                        dataKey="downloadGB"
                                        name="Download"
                                        stackId="1"
                                        stroke="#3f51b5"
                                        fill="#3f51b5"
                                        fillOpacity={0.6}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="uploadGB"
                                        name="Upload"
                                        stackId="1"
                                        stroke="#4caf50"
                                        fill="#4caf50"
                                        fillOpacity={0.6}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Daily consumption table */}
                <Grid item xs={12}>
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                                height: '6px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0,0,0,0.15)',
                                borderRadius: '4px',
                            }
                        }}
                    >
                        <Table>
                            <TableHead sx={{ backgroundColor: 'rgb(246, 248, 250)' }}>
                                <TableRow>
                                    <TableCell>Día</TableCell>
                                    {!clientId && <TableCell>Cliente</TableCell>}
                                    <TableCell align="right">Download</TableCell>
                                    <TableCell align="right">Upload</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? (
                                    data.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell component="th" scope="row">
                                                {row.date}
                                            </TableCell>
                                            {!clientId && (
                                                <TableCell>{row.clientName || ''}</TableCell>
                                            )}
                                            <TableCell align="right">
                                                <Chip
                                                    size="small"
                                                    label={`${Math.round(parseFloat(row.download || 0) * 100) / 100} GB`}
                                                    sx={{ backgroundColor: 'rgba(63, 81, 181, 0.1)', fontWeight: 500 }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    size="small"
                                                    label={`${Math.round(parseFloat(row.upload || 0) * 100) / 100} GB`}
                                                    sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', fontWeight: 500 }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    size="small"
                                                    label={`${Math.round((parseFloat(row.download || 0) + parseFloat(row.upload || 0)) * 100) / 100} GB`}
                                                    sx={{ fontWeight: 500 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={!clientId ? 5 : 4} align="center">
                                            No hay datos disponibles
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
}

export default DailyTraffic;